import type { MutableRefObject } from 'react'
import { addDays, format, intervalToDuration } from 'date-fns'
import type { TFunction } from 'i18next'
import { formatDuration, formatDurationUnit } from 'shared/utils'
import type {
  Control,
  FieldError,
  FieldErrors,
  FieldValues,
  UseFormClearErrors,
  UseFormSetValue,
  UseFormTrigger,
  UseFormWatch,
} from 'react-hook-form'
import { RoundMode } from '../DatePicker'
import {
  DAY_TRANSLATION_KEYS,
  Frequency,
  combineDateAndTime,
  expandWeekdayRange,
  getReferenceScheduleDates,
  getRecurringScheduleDurationMs,
  getWeekdayRangeEnds,
  sortWeekdays,
  isDateAtLocalMidnight,
} from 'shared/utils/schedule'

export const DATETIME_PICKER_MINUTES_STEP = 1

export const SCHEDULE_ACCESS_TIME_MINUTES_STEP = 15

export const scheduleAccessDatetimeStep = {
  minutesStep: SCHEDULE_ACCESS_TIME_MINUTES_STEP,
  roundMode: RoundMode.Nearest,
} as const

export function createScheduleShouldDisableTime(minutesStep: number) {
  return (timeValue: number, clockType: string) =>
    clockType === 'minutes' && timeValue % minutesStep !== 0
}

export const radioOptionIndentSx = {
  pl: { xs: 1, sm: 1.5 },
  pr: 0,
  pt: 0.5,
  pb: 0,
  width: '100%',
  boxSizing: 'border-box' as const,
}

export const scheduleFrequencyOptionBodySx = {
  width: '100%',
  boxSizing: 'border-box' as const,
  mt: 2,
  pl: { xs: 1, sm: 3 },
  pr: 0,
  pt: 0,
  pb: 0,
}

export const fieldCaptionLabelSx = {
  position: 'static' as const,
  transform: 'none' as const,
  mb: 0.5,
  lineHeight: 1.2,
  '&::first-letter': {
    textTransform: 'uppercase',
  },
}

/** Domyślny zakres „co tydzień”, gdy lista dni jest pusta. */
export const DEFAULT_WEEKLY_ISO_DAYS = expandWeekdayRange('MO', 'MO')

export interface ScheduleFieldNames {
  scheduleType: string
  frequency: string
  daysOfWeek: string
  scheduleAllDay: string
  scheduleFromDate: string
  scheduleToDate: string
  temporaryFromDate: string
  temporaryToDate: string
  repeatUntil: string
  timeZone?: string
}

export interface ScheduleValueConfig {
  scheduleConstant: string
  scheduleTemporary: string
  scheduleSchedule: string
  freqDaily: string
  freqWeekly: string
  freqSelectedDays: string
}

export interface ScheduleTranslations {
  startAccess: string
  endAccess: string
  repeatUntil: string
  scheduleRadio: string
  scheduleTimeAccessFrom: string
  scheduleTimeAccessTo: string
  scheduleTimeFrom: string
  scheduleTimeTo: string
  repeat: string
  setTime: string
  unsupportedRecurringPattern: string
  weekRangeFrom: string
  weekRangeTo: string
  frequencyDaily: string
  frequencyWeekly: string
  frequencySelectedDays: string
}

export type ScheduleErrorVisibility =
  | 'onSubmit'
  | 'onSubmitShowRangeOnly'
  | 'always'

export interface ScheduleFieldsProps<T extends FieldValues = FieldValues> {
  control: Control<T>
  setValue: UseFormSetValue<T>
  watch: UseFormWatch<T>
  clearErrors: UseFormClearErrors<T>
  trigger: UseFormTrigger<T>
  formState: { errors: FieldErrors<T>; submitCount?: number }
  fieldNames: ScheduleFieldNames
  valueConfig: ScheduleValueConfig
  t: TFunction
  clearLabel: string
  shouldShowErrors: boolean
  errorsVisibility: ScheduleErrorVisibility
  clearDaysOfWeekWhenLeavingDaily: boolean
  unsupportedRecurringPattern?: boolean
  unsupportedAlertSeverity?: 'error' | 'warning'
  disabled?: boolean
  datePickerUseShowTimezoneInLabel?: boolean
  temporaryDatesRevalidate?: () => Promise<void>
  repeatUntilRevalidate?: () => Promise<void>
  labels: ScheduleTranslations
  defaultSetValueOptions?: Parameters<UseFormSetValue<T>>[2]
  extraClearErrorsOnScheduleTypeChange?: string[]
  extraClearErrorsOnFrequencyChange?: string[]
  embeddedTimeZoneControl?: {
    timeZoneLabel: string
    options: string[]
    currentTimeZone: string
  }
  scheduleDatetimeFieldTimeZone?: string
  gridItemProps?: { xs?: number; lg?: number }
  radioGroupIds?: { scheduleType?: string; frequency?: string }
  daysOfWeekErrorField?: string
  daysOfWeekSetValueOptions?: Parameters<UseFormSetValue<T>>[2]
  scheduleAllDayCheckboxUsesSetValue?: boolean
  scheduleTimePickerMinutesStep?: number
  resetInactiveScheduleBranchValuesOnTypeChange?: boolean
  clearScheduleErrorsAfterStructureRevalidate?: boolean
  initialScheduleSnapshot?: Partial<T>
}

function fieldErrorToMessage(raw: unknown): string | undefined {
  if (raw == null) return undefined
  if (typeof raw === 'string') return raw
  if (typeof raw !== 'object') return undefined
  const o = raw as {
    message?: unknown
    types?: Record<string, string | string[] | undefined>
  }
  const m = o.message
  if (typeof m === 'string') return m
  if (Array.isArray(m) && typeof m[0] === 'string') return m[0]
  if (m != null && typeof m !== 'object') return String(m)
  const types = o.types
  if (types && typeof types === 'object') {
    for (const t of Object.values(types)) {
      if (typeof t === 'string') return t
      if (Array.isArray(t) && typeof t[0] === 'string') return t[0]
    }
  }
  return undefined
}

export function isYupCrossFieldValidationTestError(raw: unknown): boolean {
  if (raw == null || typeof raw !== 'object') return false
  const e = raw as FieldError & { types?: Record<string, unknown> }
  if (typeof e.type === 'string' && e.type.includes('validation')) return true
  if (e.types && typeof e.types === 'object') {
    return Object.keys(e.types).some(k => k.includes('validation'))
  }
  return false
}

export function getScheduleFieldsErrorMessage(
  errors: FieldErrors,
  key: string,
  shouldShowErrors: boolean,
  visibility: ScheduleErrorVisibility
): string | undefined {
  const raw = errors[key as keyof FieldErrors]
  const msg = fieldErrorToMessage(raw)
  if (visibility === 'always') return msg
  if (visibility === 'onSubmitShowRangeOnly') {
    if (shouldShowErrors) return msg
    if (!msg) return undefined
    return isYupCrossFieldValidationTestError(raw) ? msg : undefined
  }
  return shouldShowErrors ? msg : undefined
}

export function getScheduleDaysOfWeekPickerError(
  errors: FieldErrors,
  daysOfWeekErrorField: string,
  errorsVisibility: ScheduleErrorVisibility,
  shouldShowErrors: boolean
): FieldError[] | undefined {
  const e = (errors as Record<string, unknown>)[daysOfWeekErrorField]
  if (errorsVisibility === 'always') return e as FieldError[] | undefined
  return shouldShowErrors ? (e as FieldError[] | undefined) : undefined
}

function formatScheduleDurationWithCount(
  durationMs: number,
  t: TFunction
): string {
  const duration = intervalToDuration({ start: 0, end: durationMs })
  const parts: string[] = []

  const append = (unit: 'day' | 'hour' | 'minute', count: number) => {
    if (!count) return
    let unitLabel = formatDurationUnit(
      unit,
      count,
      t as Parameters<typeof formatDurationUnit>[2]
    )
    if (!/^\d/.test(unitLabel.trim())) {
      unitLabel = `${count} ${unitLabel}`
    }
    parts.push(unitLabel)
  }

  append('day', duration.days ?? 0)
  append('hour', duration.hours ?? 0)
  append('minute', duration.minutes ?? 0)

  if (parts.length > 0) {
    return parts.join(' ')
  }

  return formatDuration(duration, t as Parameters<typeof formatDuration>[1])
}

export function formatScheduleSetTimeLabel(
  durationMs: number | null | undefined,
  setTimeLabel: string,
  t: TFunction,
  options?: { alwaysShowLabel?: boolean }
): string | null {
  const label = setTimeLabel

  if (durationMs == null || durationMs <= 0) {
    return options?.alwaysShowLabel ? `${label}:` : null
  }

  const formatted = formatScheduleDurationWithCount(durationMs, t)
  if (!formatted) {
    return options?.alwaysShowLabel ? `${label}:` : null
  }

  return `${label}: ${formatted}`
}

export function computeScheduleDayState(
  daysOfWeekRaw: unknown,
  frequency: string,
  valueConfig: ScheduleValueConfig
) {
  const selectedDaysOfWeek = Array.isArray(daysOfWeekRaw) ? daysOfWeekRaw : []
  const sortedDaysOfWeek = sortWeekdays(selectedDaysOfWeek)
  const weekArc =
    frequency === valueConfig.freqWeekly && selectedDaysOfWeek.length > 0
      ? getWeekdayRangeEnds(selectedDaysOfWeek)
      : null
  const firstSelectedDay = weekArc ? weekArc.from : sortedDaysOfWeek[0]
  const lastSelectedDay =
    frequency === valueConfig.freqWeekly
      ? weekArc
        ? weekArc.to
        : sortedDaysOfWeek[sortedDaysOfWeek.length - 1]
      : firstSelectedDay
  const shouldShowScheduleTimeFields =
    frequency === valueConfig.freqDaily || selectedDaysOfWeek.length > 0
  return {
    selectedDaysOfWeek,
    sortedDaysOfWeek,
    firstSelectedDay,
    lastSelectedDay,
    shouldShowScheduleTimeFields,
  }
}

export function getScheduleTimeFieldLabel(
  fieldType: 'from' | 'to',
  dayOfWeek: string | undefined,
  frequency: string,
  valueConfig: ScheduleValueConfig,
  t: TFunction,
  timeLabels: Pick<
    ScheduleTranslations,
    | 'scheduleTimeAccessFrom'
    | 'scheduleTimeAccessTo'
    | 'scheduleTimeFrom'
    | 'scheduleTimeTo'
  >
): string {
  const {
    scheduleTimeAccessFrom,
    scheduleTimeAccessTo,
    scheduleTimeFrom,
    scheduleTimeTo,
  } = timeLabels

  if (frequency === valueConfig.freqDaily) {
    return fieldType === 'from' ? scheduleTimeAccessFrom : scheduleTimeAccessTo
  }

  const dayLabel = dayOfWeek ? t(DAY_TRANSLATION_KEYS[dayOfWeek]) : ''

  if (frequency === valueConfig.freqSelectedDays) {
    return fieldType === 'from' ? scheduleTimeFrom : scheduleTimeTo
  }

  return `${dayLabel} ${
    fieldType === 'from' ? scheduleTimeFrom : scheduleTimeTo
  }`
}

export function buildTemporaryDatePickerSharedProps(
  datePickerUseShowTimezoneInLabel: boolean,
  scheduleDatetimeFieldTimeZone?: string
) {
  const timeZone = scheduleDatetimeFieldTimeZone?.trim() ?? ''

  return {
    showTime: true as const,
    step: scheduleAccessDatetimeStep,
    showTimezoneInLabel: false as const,
    showTimezoneLabel: false as const,
    ...(datePickerUseShowTimezoneInLabel && timeZone
      ? {
          showTimeZoneInInput: true as const,
          displayTimeZone: timeZone,
        }
      : {}),
  }
}

export function buildRepeatUntilDatePickerSharedProps(
  datePickerUseShowTimezoneInLabel: boolean,
  scheduleDatetimeFieldTimeZone?: string
) {
  const timeZone = scheduleDatetimeFieldTimeZone?.trim() ?? ''

  return {
    useDateFormat: true as const,
    showTimezoneInLabel: false as const,
    showTimezoneLabel: false as const,
    ...(datePickerUseShowTimezoneInLabel && timeZone
      ? {
          showTimeZoneInInput: true as const,
          displayTimeZone: timeZone,
        }
      : {}),
  }
}

export type RepeatUntilDatePickerSharedProps = ReturnType<
  typeof buildRepeatUntilDatePickerSharedProps
>

export type ScheduleTimesBeforeAllDayRef = {
  from: Date | null
  to: Date | null
} | null

export function applyScheduleDateValuesUpdate(params: {
  fieldNames: ScheduleFieldNames
  valueConfig: ScheduleValueConfig
  setVal: (name: string, value: unknown, opts?: object) => void
  clearErrors: (names: string | string[]) => void
  scheduleTimesBeforeAllDayRef: MutableRefObject<ScheduleTimesBeforeAllDayRef>
  scheduleFromDate: Date | null
  scheduleToDate: Date | null
  frequency: string
  scheduleAllDay: boolean
  selectedDaysOfWeek: string[]
  nextFrequency?: Frequency
  nextDaysOfWeek?: string[]
  nextScheduleAllDay?: boolean
  previousScheduleAllDay?: boolean
  nextScheduleFromDate?: Date | null
  nextScheduleToDate?: Date | null
  fromAllDayToTimed?: boolean
}) {
  const {
    fieldNames,
    valueConfig,
    setVal,
    clearErrors,
    scheduleTimesBeforeAllDayRef,
    scheduleFromDate,
    scheduleToDate,
    frequency,
    scheduleAllDay,
    selectedDaysOfWeek,
    nextFrequency = frequency as Frequency,
    nextDaysOfWeek = selectedDaysOfWeek,
    nextScheduleAllDay = scheduleAllDay,
    previousScheduleAllDay = scheduleAllDay,
    nextScheduleFromDate = scheduleFromDate,
    nextScheduleToDate = scheduleToDate,
    fromAllDayToTimed = false,
  } = params

  if (nextFrequency !== valueConfig.freqDaily && nextDaysOfWeek.length === 0) {
    setVal(fieldNames.scheduleFromDate, null)
    setVal(fieldNames.scheduleToDate, null)
    clearErrors([fieldNames.scheduleFromDate, fieldNames.scheduleToDate])
    return
  }

  const { startDate, endDate } = getReferenceScheduleDates({
    frequency: nextFrequency as Frequency,
    daysOfWeek: nextDaysOfWeek,
  })

  if (nextScheduleAllDay) {
    if (!previousScheduleAllDay) {
      scheduleTimesBeforeAllDayRef.current = {
        from: scheduleFromDate,
        to: scheduleToDate,
      }
    }
    setVal(fieldNames.scheduleFromDate, combineDateAndTime(startDate, null))
    setVal(
      fieldNames.scheduleToDate,
      addDays(combineDateAndTime(endDate, null), 1)
    )
    clearErrors([fieldNames.scheduleFromDate, fieldNames.scheduleToDate])
    return
  }

  if (fromAllDayToTimed) {
    const saved = scheduleTimesBeforeAllDayRef.current
    scheduleTimesBeforeAllDayRef.current = null
    if (saved?.from != null || saved?.to != null) {
      setVal(
        fieldNames.scheduleFromDate,
        saved.from != null ? combineDateAndTime(startDate, saved.from) : null
      )
      setVal(
        fieldNames.scheduleToDate,
        saved.to != null ? combineDateAndTime(endDate, saved.to) : null
      )
    } else {
      setVal(fieldNames.scheduleFromDate, null)
      setVal(fieldNames.scheduleToDate, null)
    }
    clearErrors([
      fieldNames.scheduleFromDate,
      fieldNames.scheduleToDate,
      fieldNames.repeatUntil,
    ])
    return
  }

  setVal(
    fieldNames.scheduleFromDate,
    nextScheduleFromDate
      ? combineDateAndTime(startDate, nextScheduleFromDate)
      : null
  )
  setVal(
    fieldNames.scheduleToDate,
    nextScheduleToDate ? combineDateAndTime(endDate, nextScheduleToDate) : null
  )
}

export function listTriggerFieldsAfterDateClear(
  fieldName: string,
  fieldNames: ScheduleFieldNames
): string[] | null {
  if (
    fieldName === fieldNames.temporaryFromDate ||
    fieldName === fieldNames.temporaryToDate
  ) {
    return [fieldNames.temporaryFromDate, fieldNames.temporaryToDate]
  }
  if (
    fieldName === fieldNames.scheduleFromDate ||
    fieldName === fieldNames.scheduleToDate
  ) {
    return [
      fieldNames.scheduleFromDate,
      fieldNames.scheduleToDate,
      fieldNames.repeatUntil,
    ]
  }
  if (fieldName === fieldNames.repeatUntil) {
    return [
      fieldNames.repeatUntil,
      fieldNames.scheduleFromDate,
      fieldNames.scheduleToDate,
    ]
  }
  return null
}

export type DaysOfWeekSnapshotRef = MutableRefObject<string[] | null>

export function applyWeeklySelectedDaysFrequencyCrossing(params: {
  prevFrequency: string
  nextFrequency: string
  valueConfig: ScheduleValueConfig
  currentDays: string[]
  snapshotForSelectedDays: DaysOfWeekSnapshotRef
  snapshotForWeekly: DaysOfWeekSnapshotRef
}): {
  applied: boolean
  nextDaysOfWeek: string[]
  extraUpdate: { nextScheduleAllDay?: boolean }
} {
  const {
    prevFrequency,
    nextFrequency,
    valueConfig,
    currentDays,
    snapshotForSelectedDays,
    snapshotForWeekly,
  } = params

  const leavingSelectedForWeekly =
    prevFrequency === valueConfig.freqSelectedDays &&
    nextFrequency === valueConfig.freqWeekly
  const leavingWeeklyForSelected =
    prevFrequency === valueConfig.freqWeekly &&
    nextFrequency === valueConfig.freqSelectedDays

  if (!leavingSelectedForWeekly && !leavingWeeklyForSelected) {
    return { applied: false, nextDaysOfWeek: currentDays, extraUpdate: {} }
  }

  if (leavingSelectedForWeekly) {
    snapshotForSelectedDays.current = [...currentDays]
    const restoredWeekly = snapshotForWeekly.current
    return {
      applied: true,
      nextDaysOfWeek: restoredWeekly ? sortWeekdays([...restoredWeekly]) : [],
      extraUpdate: {},
    }
  }

  snapshotForWeekly.current = [...currentDays]
  const restoredSelected = snapshotForSelectedDays.current
  return {
    applied: true,
    nextDaysOfWeek: restoredSelected ? sortWeekdays([...restoredSelected]) : [],
    extraUpdate: {},
  }
}

export function cloneScheduleSnapshotFieldValue(raw: unknown): unknown {
  if (raw instanceof Date) return new Date(raw.getTime())
  if (Array.isArray(raw)) return [...raw]
  return raw
}

export function resolveScheduleDatetimeTimezone(
  scheduleDatetimeFieldTimeZone?: string,
  embeddedCurrentTimeZone?: string
): string {
  const fromProp = scheduleDatetimeFieldTimeZone?.trim()
  if (fromProp) return fromProp
  return embeddedCurrentTimeZone?.trim() ?? ''
}

export function formatScheduleLabelWithTimezone(
  text: string,
  _timeZone?: string
): string {
  return text
}

export function formatScheduleTimeInputDisplay(
  value: Date | null,
  timeZone: string
): string {
  if (!value) return ''
  const time = format(value, 'HH:mm')
  const tz = timeZone.trim()
  return tz ? `${time} (${tz})` : time
}

export function scheduleTimeInputPlaceholder(timeZone: string): string {
  const tz = timeZone.trim()
  return tz ? `(${tz})` : 'hh:mm'
}

export function formatScheduleDateTimeInputDisplay(
  value: Date | null,
  timeZone: string
): string {
  if (!value) return ''
  const formatted = format(value, 'dd.MM.yyyy HH:mm')
  const tz = timeZone.trim()
  return tz ? `${formatted} (${tz})` : formatted
}

export function shouldValidateInterdependentScheduleFields(
  shouldShowErrors: boolean,
  errorsVisibility: ScheduleErrorVisibility
): boolean {
  return (
    shouldShowErrors ||
    errorsVisibility === 'always' ||
    errorsVisibility === 'onSubmitShowRangeOnly'
  )
}

export function computeWeeklyRangeSelection(selectedDaysOfWeek: string[]) {
  const rangeEnds = getWeekdayRangeEnds(selectedDaysOfWeek)
  if (rangeEnds) return { from: rangeEnds.from, to: rangeEnds.to }
  const sorted = sortWeekdays(Array.from(new Set(selectedDaysOfWeek)))
  if (sorted.length === 0) return { from: 'MO', to: 'MO' }
  const day = sorted[0]
  return { from: day, to: day }
}

export function isRecurringDisplayFrequency(
  frequency: string,
  valueConfig: ScheduleValueConfig
): boolean {
  return (
    frequency === valueConfig.freqDaily ||
    frequency === valueConfig.freqWeekly ||
    frequency === valueConfig.freqSelectedDays
  )
}

export function getEmptyScheduleTimesOnFrequencyChange(params: {
  scheduleType: string
  prevFrequency: string
  nextFrequency: string
  valueConfig: ScheduleValueConfig
  initialScheduleSnapshot?: Record<string, unknown>
  frequencyFieldName: string
}):
  | { nextScheduleFromDate: null; nextScheduleToDate: null }
  | Record<string, never> {
  const {
    scheduleType,
    prevFrequency,
    nextFrequency,
    valueConfig,
    initialScheduleSnapshot,
    frequencyFieldName,
  } = params

  if (scheduleType !== valueConfig.scheduleSchedule) return {}
  if (
    !isRecurringDisplayFrequency(prevFrequency, valueConfig) ||
    !isRecurringDisplayFrequency(nextFrequency, valueConfig)
  ) {
    return {}
  }
  if (prevFrequency === nextFrequency) return {}

  if (!initialScheduleSnapshot) {
    return {
      nextScheduleFromDate: null,
      nextScheduleToDate: null,
    }
  }

  if (nextFrequency === initialScheduleSnapshot[frequencyFieldName]) return {}

  return {
    nextScheduleFromDate: null,
    nextScheduleToDate: null,
  }
}

export function getAllDayExitOnFrequencyChange(
  prevFrequency: string,
  nextFrequency: string,
  scheduleAllDay: boolean
) {
  if (prevFrequency === nextFrequency) return {}
  return {
    nextScheduleAllDay: false,
    previousScheduleAllDay: scheduleAllDay,
    ...(scheduleAllDay ? { fromAllDayToTimed: true as const } : {}),
  }
}

export function clearScheduleStructureErrors<T extends FieldValues>(params: {
  fieldNames: ScheduleFieldNames
  clearErrors: UseFormClearErrors<T>
  extraFields?: string[]
}) {
  const { fieldNames, clearErrors, extraFields = [] } = params
  clearErrors(fieldNames.scheduleType as any)
  clearErrors(fieldNames.frequency as any)
  clearErrors(fieldNames.scheduleAllDay as any)
  clearErrors(fieldNames.temporaryFromDate as any)
  clearErrors(fieldNames.temporaryToDate as any)
  clearErrors(fieldNames.scheduleFromDate as any)
  clearErrors(fieldNames.scheduleToDate as any)
  clearErrors(fieldNames.repeatUntil as any)
  clearErrors(fieldNames.daysOfWeek as any)
  if (fieldNames.timeZone) {
    clearErrors(fieldNames.timeZone as any)
  }
  for (const field of extraFields) {
    clearErrors(field as any)
  }
}

export function applySnapshotForScheduleTypeChange(params: {
  nextScheduleType: string
  initialScheduleSnapshot: Record<string, unknown>
  fieldNames: ScheduleFieldNames
  setVal: (name: string, value: unknown) => void
}) {
  const { nextScheduleType, initialScheduleSnapshot, fieldNames, setVal } =
    params
  const typeKey = fieldNames.scheduleType
  for (const key of Object.keys(initialScheduleSnapshot)) {
    if (key === typeKey) continue
    const raw = initialScheduleSnapshot[key]
    if (raw === undefined) continue
    setVal(key, cloneScheduleSnapshotFieldValue(raw))
  }
  setVal(typeKey, nextScheduleType)
}

export function restoreRecurringFieldsFromSnapshot(params: {
  nextFrequency: string
  initialScheduleSnapshot: Record<string, unknown>
  fieldNames: ScheduleFieldNames
  setVal: (name: string, value: unknown) => void
}) {
  const { nextFrequency, initialScheduleSnapshot, fieldNames, setVal } = params
  if (initialScheduleSnapshot[fieldNames.frequency] !== nextFrequency) return

  const keys = [
    fieldNames.scheduleFromDate,
    fieldNames.scheduleToDate,
    fieldNames.repeatUntil,
    fieldNames.daysOfWeek,
    fieldNames.scheduleAllDay,
  ]

  for (const key of keys) {
    const raw = initialScheduleSnapshot[key]
    if (raw === undefined) continue
    setVal(key, cloneScheduleSnapshotFieldValue(raw))
  }
}

export function resetInactiveScheduleBranchesForType(params: {
  nextType: string
  fieldNames: ScheduleFieldNames
  valueConfig: ScheduleValueConfig
  setVal: (name: string, value: unknown) => void
}) {
  const { nextType, fieldNames, valueConfig, setVal } = params

  const clearTemporaryBranch = () => {
    setVal(fieldNames.temporaryFromDate, null)
    setVal(fieldNames.temporaryToDate, null)
  }

  const clearRecurringBranch = () => {
    setVal(fieldNames.scheduleFromDate, null)
    setVal(fieldNames.scheduleToDate, null)
    setVal(fieldNames.repeatUntil, null)
    setVal(fieldNames.daysOfWeek, [])
    setVal(fieldNames.frequency, valueConfig.freqDaily)
    setVal(fieldNames.scheduleAllDay, false)
  }

  if (nextType === valueConfig.scheduleConstant) {
    clearTemporaryBranch()
    clearRecurringBranch()
    return
  }
  if (nextType === valueConfig.scheduleTemporary) {
    clearRecurringBranch()
    return
  }
  if (nextType === valueConfig.scheduleSchedule) {
    clearTemporaryBranch()
  }
}

export function runScheduleStructureRevalidation<
  T extends FieldValues
>(params: {
  shouldValidate: boolean
  suppressErrors: boolean
  fieldNames: ScheduleFieldNames
  clearStructureErrors: () => void
  trigger: UseFormTrigger<T>
}) {
  const {
    shouldValidate,
    suppressErrors,
    fieldNames,
    clearStructureErrors,
    trigger,
  } = params

  if (!shouldValidate) return

  if (suppressErrors) {
    clearStructureErrors()
    return
  }

  void trigger([
    fieldNames.scheduleType,
    fieldNames.temporaryFromDate,
    fieldNames.temporaryToDate,
    fieldNames.scheduleFromDate,
    fieldNames.scheduleToDate,
    fieldNames.repeatUntil,
    fieldNames.daysOfWeek,
    fieldNames.frequency,
  ] as any)
}

export function shouldClearDailyMidnightScheduleTimes(params: {
  disabled: boolean
  scheduleType: string
  frequency: string
  scheduleAllDay: boolean
  scheduleFromDate: Date | null
  scheduleToDate: Date | null
  valueConfig: ScheduleValueConfig
}): boolean {
  const {
    disabled,
    scheduleType,
    frequency,
    scheduleAllDay,
    scheduleFromDate,
    scheduleToDate,
    valueConfig,
  } = params

  if (disabled) return false
  if (scheduleType !== valueConfig.scheduleSchedule) return false
  if (frequency !== valueConfig.freqDaily) return false
  if (scheduleAllDay) return false
  if (!scheduleFromDate || !scheduleToDate) return false
  if (
    !isDateAtLocalMidnight(scheduleFromDate) ||
    !isDateAtLocalMidnight(scheduleToDate)
  ) {
    return false
  }
  if (scheduleFromDate.toDateString() !== scheduleToDate.toDateString()) {
    return false
  }
  return (
    getRecurringScheduleDurationMs(
      scheduleFromDate,
      scheduleToDate,
      Frequency.Daily,
      []
    ) <= 0
  )
}

export type ScheduleDateValuesUpdateOptions = {
  nextFrequency?: Frequency
  nextDaysOfWeek?: string[]
  nextScheduleAllDay?: boolean
  previousScheduleAllDay?: boolean
  nextScheduleFromDate?: Date | null
  nextScheduleToDate?: Date | null
  fromAllDayToTimed?: boolean
}

export function applyScheduleFrequencyChange(params: {
  nextFrequency: Frequency
  prevFrequency: string
  scheduleType: string
  scheduleAllDay: boolean
  selectedDaysOfWeek: string[]
  scheduleFromDate: Date | null
  scheduleToDate: Date | null
  fieldNames: ScheduleFieldNames
  valueConfig: ScheduleValueConfig
  clearDaysOfWeekWhenLeavingDaily: boolean
  initialScheduleSnapshot?: Record<string, unknown>
  defaultWeeklyDays: string[]
  defaultSetValueOptions?: object
  daysOfWeekSetValueOptions?: object
  setVal: (name: string, value: unknown, opts?: object) => void
  clearErrors: (name: string) => void
  extraClearErrorsOnFrequencyChange: string[]
  snapshotForSelectedDays: DaysOfWeekSnapshotRef
  snapshotForWeekly: DaysOfWeekSnapshotRef
  scheduleTimesBeforeWeekPatternClearRef: MutableRefObject<ScheduleTimesBeforeAllDayRef>
  updateScheduleDateValues: (opts?: ScheduleDateValuesUpdateOptions) => void
  restoreRecurringFromSnapshot: (nextFrequency: string) => void
  revalidateAfterStructureChange: () => void
}) {
  const {
    nextFrequency,
    prevFrequency,
    scheduleType,
    scheduleAllDay,
    selectedDaysOfWeek,
    scheduleFromDate,
    scheduleToDate,
    fieldNames,
    valueConfig,
    clearDaysOfWeekWhenLeavingDaily,
    initialScheduleSnapshot,
    defaultWeeklyDays,
    defaultSetValueOptions,
    daysOfWeekSetValueOptions,
    setVal,
    clearErrors,
    extraClearErrorsOnFrequencyChange,
    snapshotForSelectedDays,
    snapshotForWeekly,
    scheduleTimesBeforeWeekPatternClearRef,
    updateScheduleDateValues,
    restoreRecurringFromSnapshot,
    revalidateAfterStructureChange,
  } = params

  const allDayExitOnFrequencyChange = getAllDayExitOnFrequencyChange(
    prevFrequency,
    nextFrequency,
    scheduleAllDay
  )

  const finishFrequencyChange = () => {
    clearErrors(fieldNames.frequency)
    for (const field of extraClearErrorsOnFrequencyChange) {
      clearErrors(field)
    }
    restoreRecurringFromSnapshot(nextFrequency)
    revalidateAfterStructureChange()
  }

  setVal(fieldNames.frequency, nextFrequency)
  if (prevFrequency !== nextFrequency) {
    setVal(fieldNames.scheduleAllDay, false)
  }
  const emptyTimesPatch = getEmptyScheduleTimesOnFrequencyChange({
    scheduleType,
    prevFrequency,
    nextFrequency,
    valueConfig,
    initialScheduleSnapshot,
    frequencyFieldName: fieldNames.frequency,
  })

  const weekPatternCrossing = applyWeeklySelectedDaysFrequencyCrossing({
    prevFrequency,
    nextFrequency,
    valueConfig,
    currentDays: selectedDaysOfWeek,
    snapshotForSelectedDays,
    snapshotForWeekly,
  })

  if (weekPatternCrossing.applied) {
    let nextDays = weekPatternCrossing.nextDaysOfWeek
    if (nextFrequency === valueConfig.freqWeekly && nextDays.length === 0) {
      nextDays = [...defaultWeeklyDays]
    }
    if (nextDays.length === 0) {
      if (scheduleFromDate != null || scheduleToDate != null) {
        scheduleTimesBeforeWeekPatternClearRef.current = {
          from: scheduleFromDate,
          to: scheduleToDate,
        }
      }
    }

    setVal(fieldNames.daysOfWeek, nextDays, {
      ...defaultSetValueOptions,
      ...daysOfWeekSetValueOptions,
    })

    const stashedTimes = scheduleTimesBeforeWeekPatternClearRef.current
    const restoreStashedTimes = nextDays.length > 0 && stashedTimes != null

    updateScheduleDateValues({
      nextFrequency,
      nextDaysOfWeek: nextDays,
      ...weekPatternCrossing.extraUpdate,
      ...(restoreStashedTimes
        ? {
            nextScheduleFromDate: stashedTimes.from,
            nextScheduleToDate: stashedTimes.to,
          }
        : {}),
      ...emptyTimesPatch,
      ...allDayExitOnFrequencyChange,
    })

    if (restoreStashedTimes) {
      scheduleTimesBeforeWeekPatternClearRef.current = null
    }
    finishFrequencyChange()
    return
  }

  const { nextDaysOfWeek, extraUpdate, clearDaysOfWeekFormField } =
    resolveFrequencyChangeScheduleArgs(
      clearDaysOfWeekWhenLeavingDaily,
      nextFrequency,
      valueConfig,
      selectedDaysOfWeek
    )

  const effectiveDays =
    nextFrequency === valueConfig.freqWeekly && nextDaysOfWeek.length === 0
      ? [...defaultWeeklyDays]
      : nextDaysOfWeek

  const shouldWriteDaysToForm =
    clearDaysOfWeekFormField ||
    (nextFrequency === valueConfig.freqWeekly && nextDaysOfWeek.length === 0)

  if (shouldWriteDaysToForm) {
    setVal(fieldNames.daysOfWeek, effectiveDays, {
      ...defaultSetValueOptions,
      ...daysOfWeekSetValueOptions,
    })
  }

  updateScheduleDateValues({
    nextFrequency,
    nextDaysOfWeek: effectiveDays,
    ...extraUpdate,
    ...emptyTimesPatch,
    ...allDayExitOnFrequencyChange,
  })

  finishFrequencyChange()
}

export function resolveFrequencyChangeScheduleArgs(
  clearDaysOfWeekWhenLeavingDaily: boolean,
  nextFrequency: string,
  valueConfig: ScheduleValueConfig,
  selectedDaysOfWeek: string[]
): {
  nextDaysOfWeek: string[]
  extraUpdate: { nextScheduleAllDay?: boolean }
  clearDaysOfWeekFormField: boolean
} {
  if (clearDaysOfWeekWhenLeavingDaily) {
    if (nextFrequency !== valueConfig.freqDaily) {
      return {
        nextDaysOfWeek: [],
        extraUpdate: {},
        clearDaysOfWeekFormField: true,
      }
    }
    return {
      nextDaysOfWeek: [],
      extraUpdate: { nextScheduleAllDay: false },
      clearDaysOfWeekFormField: false,
    }
  }
  if (nextFrequency === valueConfig.freqDaily) {
    return {
      nextDaysOfWeek: selectedDaysOfWeek,
      extraUpdate: { nextScheduleAllDay: false },
      clearDaysOfWeekFormField: false,
    }
  }
  return {
    nextDaysOfWeek: selectedDaysOfWeek,
    extraUpdate: {},
    clearDaysOfWeekFormField: false,
  }
}
